import React, { useEffect, useState, useCallback } from "react";
import Layout from "../../components/Layout";

import { LogsService, LogItem } from "../../services/logsService";
import {
  Info,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  Clock,
  Activity as ActivityIcon,
  UserRound,
} from "lucide-react";
import "../../styles/member/adminLogs.css";
import { formatDateTime } from "../../utils/dateFormatter";
import emtyDataImg from "../../assets/icons/empty-data.svg";

const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="admin-logs__title">{children}</h3>
);
const Text: React.FC<{ children: React.ReactNode; title?: string }> = ({
  children,
  title,
}) => <span title={title}>{children}</span>;

const levelColor: Record<string, string> = {
  INFO: "blue",
  WARNING: "orange",
  ERROR: "red",
  SUCCESS: "green",
};

const resultColor: Record<string, string> = {
  SUCCESS: "green",
  FAILURE: "red",
  PENDING: "gold",
};

const activityLabelVi: Record<string, string> = {
  LOGIN: "Đăng nhập",
  LOGOUT: "Đăng xuất",
  REGISTER: "Đăng ký",
  PASSWORD_RESET: "Đặt lại mật khẩu",
  PASSWORD_CHANGE: "Đổi mật khẩu",
  USER_CREATE: "Tạo người dùng",
  USER_UPDATE: "Cập nhật người dùng",
  USER_DELETE: "Xóa người dùng",
  USER_ACTIVATE: "Kích hoạt người dùng",
  USER_DEACTIVATE: "Vô hiệu hóa người dùng",
  ROLE_ASSIGN: "Gán vai trò",
  POST_CREATE: "Tạo bài viết",
  POST_UPDATE: "Cập nhật bài viết",
  POST_DELETE: "Xóa bài viết",
  POST_PUBLISH: "Xuất bản bài viết",
  POST_UNPUBLISH: "Gỡ xuất bản bài viết",
  CATEGORY_CREATE: "Tạo danh mục",
  CATEGORY_UPDATE: "Cập nhật danh mục",
  CATEGORY_DELETE: "Xóa danh mục",
  TRANSACTION_CREATE: "Tạo giao dịch",
  TRANSACTION_UPDATE: "Cập nhật giao dịch",
  TRANSACTION_DELETE: "Xóa giao dịch",
  TRANSACTION_COMPLETE: "Hoàn tất giao dịch",
  PROFILE_UPDATE: "Cập nhật hồ sơ",
  AVATAR_UPLOAD: "Tải lên ảnh đại diện",
  FOLLOW_USER: "Theo dõi người dùng",
  UNFOLLOW_USER: "Bỏ theo dõi người dùng",
  SHARE_POST: "Chia sẻ bài viết",
  UNSHARE_POST: "Bỏ chia sẻ bài viết",
  SEARCH_PERFORMED: "Tìm kiếm",
  ADMIN_ACTION: "Hành động quản trị",
  SYSTEM_ERROR: "Lỗi hệ thống",
  SYSTEM_WARNING: "Cảnh báo hệ thống",
};

const resultLabelVi: Record<string, string> = {
  SUCCESS: "Thành công",
  FAILURE: "Thất bại",
  PENDING: "Đang xử lý",
};

const AdminLogs: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [moduleName, setModuleName] = useState("");
  const [activityType, setActivityType] = useState("");
  const [logLevel, setLogLevel] = useState("");
  const [result, setResult] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [activityTypeOpen, setActivityTypeOpen] = useState(false);
  const [logLevelOpen, setLogLevelOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);


  // Debounce search input
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true);
    }
    
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LogsService.getAdminLogs({
        page,
        limit,
        search: debouncedSearch || undefined,
        activityType: activityType || undefined,
        logLevel: logLevel || undefined,
        result: result || undefined,
        module: moduleName || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setLogs(res.data as any);
      setTotal(res.total);
    } catch (e: any) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, moduleName, activityType, logLevel, result, fromDate, toDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const onApplyFilters = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setPage(1);
  };

  const columns = [
    {
      title: "Thời gian",
      dataIndex: "createdAt",
      key: "createdAt",
      className: "col-time",
      render: (val: string) => formatDateTime(val),
    },
    {
      title: "Loại",
      dataIndex: "activityType",
      key: "activityType",
      className: "col-type",
      render: (val: string) => (
        <span className="tag">
          <ActivityIcon size={14} className="admin-logs__icon" />
          {val}
        </span>
      ),
    },
    {
      title: "Level",
      dataIndex: "logLevel",
      key: "logLevel",
      className: "col-level",
      render: (val: string) => {
        const Icon =
          val === "INFO"
            ? Info
            : val === "WARNING"
            ? AlertTriangle
            : val === "ERROR"
            ? XCircle
            : val === "SUCCESS"
            ? CheckCircle2
            : Info;
        return (
          <span
            className={`tag tag-${(
              levelColor[val] || "default"
            ).toLowerCase()}`}
          >
            <Icon size={14} className="admin-logs__icon" />
            {val}
          </span>
        );
      },
    },
    {
      title: "Kết quả",
      dataIndex: "result",
      key: "result",
      className: "col-result",
      render: (val: string) => {
        const Icon =
          val === "SUCCESS"
            ? CheckCircle2
            : val === "FAILURE"
            ? XCircle
            : Clock;
        return (
          <span
            className={`tag tag-${(
              resultColor[val] || "default"
            ).toLowerCase()}`}
          >
            <Icon size={14} className="admin-logs__icon" />
            {val}
          </span>
        );
      },
    },
    {
      title: "Mô tả / Lỗi",
      key: "description",
      ellipsis: true,
      className: "col-desc",
      render: (_: any, record: LogItem) => {
        const vnActivity =
          activityLabelVi[record.activityType] || record.activityType;
        const vnResult = resultLabelVi[record.result] || record.result;
        const main = record.errorMessage
          ? `Lỗi: ${record.errorMessage}`
          : `${vnActivity}${
              record.action ? ` - ${record.action}` : ""
            } (${vnResult})`;
        const tooltip = record.description || record.errorMessage || main;
        return <Text title={tooltip}>{main}</Text>;
      },
    },
    {
      title: "User",
      key: "user",
      className: "col-user",
      render: (_: any, record: LogItem) => (
        <span>
          <UserRound size={14} className="admin-logs__icon" />
          {record.user?.email || record.userId || "-"}
        </span>
      ),
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      className: "col-module",
      render: (val: string) => val || "-",
    },
    {
      title: "IP",
      dataIndex: "ipAddress",
      key: "ipAddress",
      className: "col-ip",
      render: (val: string) => val || "-",
    },
    {
      // title: "Action",
      // dataIndex: "action",
      // key: "action",
      // className: "col-action",
      render: (val: string) => val || "-",
    },
    
  ];

  const activityTypeOptions = [
    { value: "LOGIN", label: "Đăng nhập" },
    { value: "LOGOUT", label: "Đăng xuất" },
    { value: "REGISTER", label: "Đăng ký" },
    { value: "PASSWORD_RESET", label: "Đặt lại mật khẩu" },
    { value: "PASSWORD_CHANGE", label: "Đổi mật khẩu" },
    { value: "USER_CREATE", label: "Tạo người dùng" },
    { value: "USER_UPDATE", label: "Cập nhật người dùng" },
    { value: "USER_DELETE", label: "Xóa người dùng" },
    { value: "USER_ACTIVATE", label: "Kích hoạt người dùng" },
    { value: "USER_DEACTIVATE", label: "Vô hiệu hóa người dùng" },
    { value: "ROLE_ASSIGN", label: "Gán vai trò" },
    { value: "POST_CREATE", label: "Tạo bài viết" },
    { value: "POST_UPDATE", label: "Cập nhật bài viết" },
    { value: "POST_DELETE", label: "Xóa bài viết" },
    { value: "POST_PUBLISH", label: "Xuất bản bài viết" },
    { value: "POST_UNPUBLISH", label: "Gỡ xuất bản bài viết" },
    { value: "CATEGORY_CREATE", label: "Tạo danh mục" },
    { value: "CATEGORY_UPDATE", label: "Cập nhật danh mục" },
    { value: "CATEGORY_DELETE", label: "Xóa danh mục" },
    { value: "TRANSACTION_CREATE", label: "Tạo giao dịch" },
    { value: "TRANSACTION_UPDATE", label: "Cập nhật giao dịch" },
    { value: "TRANSACTION_DELETE", label: "Xóa giao dịch" },
    { value: "TRANSACTION_COMPLETE", label: "Hoàn tất giao dịch" },
    { value: "PROFILE_UPDATE", label: "Cập nhật hồ sơ" },
    { value: "AVATAR_UPLOAD", label: "Tải lên ảnh đại diện" },
    { value: "FOLLOW_USER", label: "Theo dõi người dùng" },
    { value: "UNFOLLOW_USER", label: "Bỏ theo dõi người dùng" },
    { value: "SHARE_POST", label: "Chia sẻ bài viết" },
    { value: "UNSHARE_POST", label: "Bỏ chia sẻ bài viết" },
    { value: "SEARCH_PERFORMED", label: "Tìm kiếm" },
    { value: "ADMIN_ACTION", label: "Hành động quản trị" },
    { value: "SYSTEM_ERROR", label: "Lỗi hệ thống" },
    { value: "SYSTEM_WARNING", label: "Cảnh báo hệ thống" },
  ];

  const logLevelOptions = [
    { value: "INFO", label: "Thông tin" },
    { value: "WARNING", label: "Cảnh báo" },
    { value: "ERROR", label: "Lỗi" },
    { value: "SUCCESS", label: "Thành công" },
  ];

  const resultOptions = [
    { value: "SUCCESS", label: "Thành công" },
    { value: "FAILURE", label: "Thất bại" },
    { value: "PENDING", label: "Đang xử lý" },
  ];

  return (
    <Layout>
      <div className="admin-logs">
        <div className="admin-logs__content">
          <div className="card">
            <div className="card__header">
              <Title>Nhật ký hoạt động</Title>
              <div>
                <button
                  className="btn"
                  type="button"
                  onClick={() => fetchLogs()}
                  disabled={loading || isSearching}
                >
                  Tải lại
                </button>
              </div>
            </div>
            <div className="card__body">
              <form className="admin-logs__form" onSubmit={onApplyFilters}>
                <div className="admin-logs__row">
                  <div className="form-item col-6">
                    <label>Tìm kiếm</label>
                    <input
                      className={`input ${isSearching ? 'searching' : ''}`}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Nhập mô tả, lỗi, email..."
                    />
                  </div>
                  <div className="form-item col-6 form-item--date">
                    <label>Khoảng thời gian</label>
                    <div className="date-range">
                      <input
                        className="input"
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                      <span className="date-sep">→</span>
                      <input
                        className="input"
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="admin-logs__row">
                  <div className="form-item col-3">
                    <label>Module</label>
                    <input
                      className="input"
                      value={moduleName}
                      onChange={(e) => setModuleName(e.target.value)}
                      placeholder="Ví dụ: auth, posts..."
                    />
                  </div>
                  <div className="form-item col-3">
                    <label>Loại hoạt động</label>
                    <div className="filter-dropdown">
                      <button
                        type="button"
                        className="dropdown-trigger"
                        onClick={() => setActivityTypeOpen((v) => !v)}
                      >
                        {
                          activityTypeOptions.find(
                            (o) => o.value === activityType
                          )?.label || "Chọn loại hoạt động"
                        }
                        <span className="dropdown-arrow">▼</span>
                      </button>
                      {activityTypeOpen && (
                        <div className="dropdown-menu-log">
                          {activityTypeOptions.map((opt) => (
                            <div
                              key={opt.value}
                              className={`dropdown-item${
                                activityType === opt.value ? " active" : ""
                              }`}
                              onClick={() => {
                                setActivityType(opt.value);
                                setActivityTypeOpen(false);
                              }}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-item col-3">
                    <label>Mức log</label>
                    <div className="filter-dropdown">
                      <button
                        type="button"
                        className="dropdown-trigger"
                        onClick={() => setLogLevelOpen((v) => !v)}
                      >
                        {
                          logLevelOptions.find((o) => o.value === logLevel)
                            ?.label || "Chọn mức log"
                        }
                        <span className="dropdown-arrow">▼</span>
                      </button>
                      {logLevelOpen && (
                        <div className="dropdown-menu">
                          {logLevelOptions.map((opt) => (
                            <div
                              key={opt.value}
                              className={`dropdown-item${
                                logLevel === opt.value ? " active" : ""
                              }`}
                              onClick={() => {
                                setLogLevel(opt.value);
                                setLogLevelOpen(false);
                              }}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-item col-3">
                    <label>Kết quả</label>
                    <div className="filter-dropdown">
                      <button
                        type="button"
                        className="dropdown-trigger"
                        onClick={() => setResultOpen((v) => !v)}
                      >
                        {
                          resultOptions.find((o) => o.value === result)?.label ||
                          "Chọn kết quả hoạt động"
                        }
                        <span className="dropdown-arrow">▼</span>
                      </button>
                      {resultOpen && (
                        <div className="dropdown-menu">
                          {resultOptions.map((opt) => (
                            <div
                              key={opt.value}
                              className={`dropdown-item${
                                result === opt.value ? " active" : ""
                              }`}
                              onClick={() => {
                                setResult(opt.value);
                                setResultOpen(false);
                              }}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="admin-logs__actions">
                  <button
                    className="btn-primary"
                    type="submit"
                    disabled={loading || isSearching}
                  >
                    Áp dụng
                  </button>
                  <button
                    className="btn-secondary"
                    type="button"
                    disabled={loading || isSearching}
                    onClick={() => {
                      setSearch("");
                      setModuleName("");
                      setActivityType("");
                      setLogLevel("");
                      setResult("");
                      setFromDate("");
                      setToDate("");
                      setPage(1);
                    }}
                  >
                    Xóa lọc
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="card admin-logs__table">
            <div className="card__body">
              <table>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key as string} className={col.className}>
                        {col.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading || isSearching ? (
                    <tr>
                      <td colSpan={columns.length}>
                        {isSearching ? "Đang tìm kiếm..." : "Đang tải..."}
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        <img src={emtyDataImg} alt="Dữ liệu trống" />
                        <div>Không có dữ liệu</div>
                      </td>
                    </tr>
                  ) : (
                    logs.map((row) => (
                      <tr key={row.id}>
                        <td className="col-time">
                          {columns[0].render!(row.createdAt, row)}
                        </td>
                        <td className="col-type">
                          {columns[1].render!(row.activityType, row)}
                        </td>
                        <td className="col-level">
                          {columns[2].render!(row.logLevel, row)}
                        </td>
                        <td className="col-result">
                          {columns[3].render!(row.result, row)}
                        </td>
                        <td className="col-desc">
                          {columns[4].render!(undefined as any, row)}
                        </td>
                        <td className="col-user">
                          {columns[5].render!(undefined as any, row)}
                        </td>
                        <td className="col-module">
                          {columns[6].render!(row.module as any, row)}
                        </td>
                        {/* <td className="col-action">
                          {columns[7].render!(row.action as any, row)}
                        </td> */}
                        <td className="col-ip">
                          {columns[8].render!(row.ipAddress as any, row)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="pagination">
                <div className="pagination__info">Tổng: {total} bản ghi</div>
                <div className="pagination__controls">
                  <button
                    className="btn"
                    disabled={page <= 1 || loading || isSearching}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Trước
                  </button>
                  <span>
                    {page}/{Math.max(1, Math.ceil(total / limit))}
                  </span>
                  <button
                    className="btn"
                    disabled={
                      page >= Math.max(1, Math.ceil(total / limit)) || loading || isSearching
                    }
                    onClick={() =>
                      setPage((p) =>
                        Math.min(Math.max(1, Math.ceil(total / limit)), p + 1)
                      )
                    }
                  >
                    Sau
                  </button>
                  <select
                    className="selectt"
                    value={limit}
                    onChange={(e) => {
                      setLimit(parseInt(e.target.value) || 20);
                      setPage(1);
                    }}
                  >
                    {[10, 20, 50, 100].map((s) => (
                      <option key={s} value={s}>
                        {s}/trang
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminLogs;
